import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Aviso,
  Botao,
  Campo,
  CampoCnpj,
  CampoDeSelecao,
  CampoTelefone,
  Modal,
  Selo,
} from '../componentes/ui';
import {
  useConsultaDeCnpj,
  type ChecagemDeCnpj,
  type DadosDeCnpj,
} from '../hooks/useConsultaDeCnpj';
import { useEmpresaCorrente, useIdentidade } from '../hooks/useSessao';
import { requisicaoAutenticada } from '../servicos/http';
import { formatarCnpj } from '../servicos/formato';
import { mascararTelefone } from '../servicos/documento';
import { useExercicioVigente } from '../servicos/exercicio';
import { listarEmpresas, opcoesDeRegime } from '../servicos/empresas';
import { CamposDeValores, itensPreenchidos } from '../componentes/LancamentoDeValores';
import { ErroDaApi } from './ErroDaApi';

interface EmpresaCriada {
  readonly id: string;
  readonly legalName: string;
}

type Etapa = 'cnpj' | 'empresa' | 'endereco' | 'valores';

/**
 * Adicionar cliente pela carteira.
 *
 * ## Por que o CNPJ vem primeiro, sozinho
 *
 * Ele responde três coisas antes de qualquer digitação: se é válido, se a
 * empresa **já existe** aqui, e como ela se chama na Receita. Pedir o cadastro
 * inteiro para só então descobrir que o cliente já estava lá é fazer a pessoa
 * digitar à toa — e, pior, convida a criar a duplicata.
 *
 * ## O que a Receita preenche, e o que ela não decide
 *
 * Razão social, endereço, telefone, e-mail, natureza jurídica e CNAE vêm da
 * consulta — que dispara sozinha quando o CNPJ fecha (lista de 15/09, itens 3
 * e 10), e a etapa seguinte abre com tudo preenchido. Tudo continua editável:
 * o cadastro fiscal atrasa, e quem está na frente do cliente sabe mais que a
 * base pública. Se a consulta falhar — os serviços gratuitos limitam por IP —,
 * os campos ficam em branco e o cadastro segue à mão.
 *
 * O regime tributário **não** é preenchido: a consulta pública não diz se a
 * empresa é optante do Simples, e chutar "Lucro Presumido" trocaria a tabela de
 * utilização do cálculo em silêncio.
 *
 * ## Os valores entram no mesmo assistente
 *
 * Cliente cadastrado sem apuração é cliente que não aparece em relatório
 * nenhum — e o SPED raramente está à mão na hora do cadastro. A última etapa
 * pede o ICMS apurado de cada mês e lança as competências junto (RN-085), com
 * a mesma conta do upload.
 *
 * E quando a empresa **já existe** e o que falta são só os valores, o seletor
 * do topo a escolhe: o assistente pula direto para a etapa de valores, em vez
 * de obrigar a sair daqui, achar o cliente na carteira e abrir outra tela.
 *
 * ## O mesmo assistente cadastra empresa sem papel
 *
 * "Nova empresa" na tela de Empresas usa este componente com `modo="empresa"`:
 * o fluxo (CNPJ → dados → endereço) é o mesmo, o que muda é o passo final —
 * não marca papel de cliente e abre o cadastro em vez do perfil. Antes eram
 * dois formulários, e o de empresa não consultava a Receita.
 */
export function AdicionarCliente(props: {
  readonly aberto: boolean;
  readonly aoFechar: () => void;
  /** `cliente` (padrão) marca o papel e abre o perfil; `empresa` só cadastra. */
  readonly modo?: 'cliente' | 'empresa';
}): JSX.Element {
  const navegar = useNavigate();
  const queryClient = useQueryClient();
  const identidade = useIdentidade();
  const empresaCorrente = useEmpresaCorrente();
  const comoCliente = (props.modo ?? 'cliente') === 'cliente';

  const [etapa, setEtapa] = useState<Etapa>('cnpj');
  const [cnpj, setCnpj] = useState('');
  const [checagem, setChecagem] = useState<ChecagemDeCnpj | null>(null);
  const [consultado, setConsultado] = useState<DadosDeCnpj | null>(null);

  /**
   * `''` = nada escolhido ainda (o seletor abre em branco), `'nova'` = cadastrar
   * pelo CNPJ, qualquer outro valor = o id da empresa que já existe.
   *
   * O seletor só existe no modo cliente. No modo empresa não há o que escolher —
   * é sempre cadastro novo. Sem isto a escolha ficava em `''` para sempre: o
   * campo de CNPJ não aparecia e o "Continuar" não destravava.
   */
  const [escolhaNoSeletor, setEscolha] = useState('');
  const escolha = comoCliente ? escolhaNoSeletor : 'nova';
  const empresaExistente = escolha === 'nova' ? '' : escolha;
  const operacao = useExercicioVigente();
  const [anoSped, setAnoSped] = useState(String(operacao - 1));
  const [valores, setValores] = useState<Record<string, string>>({});

  const empresas = useQuery({
    queryKey: ['empresas', 'seletor'],
    // 100 é o teto da listagem; acima disso o servidor recusa a página.
    queryFn: () => listarEmpresas({ porPagina: 100 }),
    enabled: props.aberto && comoCliente,
  });

  const itens = useMemo(() => itensPreenchidos(valores, anoSped), [valores, anoSped]);

  const [form, setForm] = useState({
    legalName: '',
    name: '',
    emailAddress: '',
    phone: '',
    taxRegime: '',
    industryClassification: '',
    cadIcms: '',
    credencialSiscred: '',
    addressPostalCode: '',
    addressStreetName: '',
    addressStreetNumber: '',
    addressExtraInfo: '',
    addressDistrict: '',
    addressCity: '',
    addressStateCode: '',
  });

  const definir = (campo: keyof typeof form, valor: string): void =>
    setForm((f) => ({ ...f, [campo]: valor }));

  const digitos = cnpj.replace(/\D/g, '');

  /**
   * Valida, checa duplicata e, se der, preenche com o que a Receita sabe —
   * sozinha, assim que o número fecha. Empresa já cadastrada não vai à
   * Receita: o aviso oferece ir até ela.
   */
  const consultar = useConsultaDeCnpj(cnpj, {
    ativa: etapa === 'cnpj' && props.aberto,
    consultarSeCadastrada: false,
    aoChecar: setChecagem,
    aoCarregar: (dados, checagemDaConsulta) => {
      setConsultado(dados);
      setForm((f) => ({
        ...f,
        legalName: dados.razaoSocial,
        name: dados.nomeFantasia ?? '',
        emailAddress: dados.email ?? '',
        // Mascarado ao entrar: a Receita devolve `(43) 3047-0020 / (43)
        // 99999-8888`, e o campo mostrava só o primeiro número enquanto o
        // formulário mandava a string inteira — e o cadastro morria com "no
        // máximo 20 caracteres" sobre um telefone que na tela tinha 14.
        phone: mascararTelefone(dados.telefone ?? ''),
        taxRegime: dados.regimeTributario ?? '',
        industryClassification: dados.cnaePrincipal ?? '',
        addressPostalCode: dados.endereco.cep ?? '',
        addressStreetName: dados.endereco.logradouro ?? '',
        addressStreetNumber: dados.endereco.numero ?? '',
        addressExtraInfo: dados.endereco.complemento ?? '',
        addressDistrict: dados.endereco.bairro ?? '',
        addressCity: dados.endereco.cidade ?? '',
        addressStateCode: dados.endereco.uf ?? '',
      }));
      /*
        RN-306 — cadastro desativado não avança: quem precisa aparecer é o
        aviso de reativar, na etapa do CNPJ. Sem esta guarda o assistente
        pulava para os dados da empresa e o operador cadastrava a duplicata
        que o servidor recusaria no fim.
      */
      if (checagemDaConsulta?.cadastroAtivo !== false) setEtapa('empresa');
    },
  });

  const criar = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<EmpresaCriada>('/business', {
        metodo: 'POST',
        corpo: {
          taxpayerId: digitos,
          legalName: form.legalName,
          emailAddress: form.emailAddress,
          ...(form.name === '' ? {} : { name: form.name }),
          ...(form.phone === '' ? {} : { phone: form.phone }),
          ...(form.taxRegime === '' ? {} : { taxRegime: form.taxRegime }),
          ...(form.industryClassification === ''
            ? {}
            : { industryClassification: form.industryClassification }),
          ...(form.cadIcms === '' ? {} : { cadIcms: form.cadIcms }),
          ...(form.credencialSiscred === '' ? {} : { credencialSiscred: form.credencialSiscred }),
          ...(form.addressPostalCode === '' ? {} : { addressPostalCode: form.addressPostalCode }),
          ...(form.addressStreetName === '' ? {} : { addressStreetName: form.addressStreetName }),
          ...(form.addressStreetNumber === ''
            ? {}
            : { addressStreetNumber: form.addressStreetNumber }),
          ...(form.addressExtraInfo === '' ? {} : { addressExtraInfo: form.addressExtraInfo }),
          ...(form.addressDistrict === '' ? {} : { addressDistrict: form.addressDistrict }),
          ...(form.addressCity === '' ? {} : { addressCity: form.addressCity }),
          ...(form.addressStateCode === '' ? {} : { addressStateCode: form.addressStateCode }),
        },
      }),
    onSuccess: (empresa) => concluir(empresa.id),
  });

  /**
   * O que acontece depois de existir uma empresa: o papel de cliente e, quando
   * houver, as competências lançadas.
   *
   * É o mesmo fim para os dois caminhos — a empresa recém-criada e a escolhida
   * no seletor —, e por isso mora num lugar só.
   */
  const concluir = async (businessId: string): Promise<void> => {
    /**
     * Criar a empresa não a torna cliente — papel é outro cadastro (RN-015).
     * Sem este passo ela ficaria em "Empresas" e nunca apareceria na carteira
     * de clientes, que é de onde a pessoa clicou "Adicionar". No modo empresa
     * o papel fica para a aba Serviços, que é onde se decide se ela é cliente,
     * fornecedor ou assessor.
     */
    if (comoCliente) {
      await requisicaoAutenticada('/icms/settings', {
        metodo: 'POST',
        corpo: {
          businessId,
          cliente: true,
          // RN-621 — o cliente nasce vinculado ao assessor que o cadastra: a
          // empresa em que a pessoa está trabalhando agora. O admin não manda
          // nada, e o servidor deixa a operadora.
          ...(identidade?.isAdmin === true || empresaCorrente === null
            ? {}
            : { assessorId: empresaCorrente }),
        },
      });
    }

    // RN-085 — as competências digitadas na última etapa do assistente.
    if (comoCliente && itens.length > 0) {
      await requisicaoAutenticada('/icms/speds/manual', {
        metodo: 'POST',
        corpo: { clienteId: businessId, itens },
      });
    }

    void queryClient.invalidateQueries({ queryKey: ['carteira'] });
    void queryClient.invalidateQueries({ queryKey: ['empresas'] });
    void queryClient.invalidateQueries({ queryKey: ['speds', businessId] });
    fechar();
    // Cliente abre no perfil; empresa, no cadastro — o passo seguinte é
    // vincular colaborador ou definir o papel.
    navegar(comoCliente ? `/clientes/${businessId}` : `/empresas/${businessId}`);
  };

  /** O caminho curto: a empresa já existe, só faltam os valores. */
  const lancarNaExistente = useMutation({ mutationFn: () => concluir(empresaExistente) });

  /**
   * RN-306 — a empresa desativada volta em vez de virar duplicata.
   *
   * O CNPJ é único no cadastro: desativar não libera o número, e tentar
   * cadastrar de novo batia em "já existe" sem saída nenhuma. Aqui o cadastro
   * antigo é reativado — com o histórico dele — e o assistente segue para os
   * valores.
   */
  const reativar = useMutation({
    mutationFn: async () => {
      const empresa = await requisicaoAutenticada<{ id: string } | null>(
        `/business/por-cnpj/${digitos}`,
      );
      if (empresa === null) throw new Error('Empresa não encontrada pelo CNPJ.');
      await requisicaoAutenticada(`/business/${empresa.id}/reativar`, { metodo: 'POST' });
      return empresa.id;
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({ queryKey: ['empresas'] });
      // O modo empresa não tem etapa de valores: o cadastro reativado abre na
      // tela dele, como abriria um recém-criado.
      if (!comoCliente) {
        fechar();
        navegar(`/empresas/${id}`);
        return;
      }
      setEscolha(id);
      setEtapa('valores');
    },
  });

  const fechar = (): void => {
    criar.reset();
    lancarNaExistente.reset();
    setEtapa('cnpj');
    setCnpj('');
    setEscolha('');
    setValores({});
    reativar.reset();
    setChecagem(null);
    setConsultado(null);
    setForm({
      legalName: '',
      name: '',
      emailAddress: '',
      phone: '',
      taxRegime: '',
      industryClassification: '',
      cadIcms: '',
      credencialSiscred: '',
      addressPostalCode: '',
      addressStreetName: '',
      addressStreetNumber: '',
      addressExtraInfo: '',
      addressDistrict: '',
      addressCity: '',
      addressStateCode: '',
    });
    props.aoFechar();
  };

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={fechar}
      titulo={comoCliente ? 'Adicionar cliente' : 'Nova empresa'}
      descricao={etapa === 'cnpj' ? 'Comece pelo CNPJ' : formatarCnpj(digitos)}
      largura="larga"
      rodape={
        <>
          <Botao variante="secundario" onClick={fechar}>
            Cancelar
          </Botao>
          {etapa === 'empresa' && (
            <Botao variante="secundario" onClick={() => setEtapa('cnpj')}>
              Voltar
            </Botao>
          )}
          {etapa === 'endereco' && (
            <Botao variante="secundario" onClick={() => setEtapa('empresa')}>
              Voltar
            </Botao>
          )}
          {etapa === 'valores' && (
            <Botao
              variante="secundario"
              onClick={() => setEtapa(empresaExistente === '' ? 'endereco' : 'cnpj')}
            >
              Voltar
            </Botao>
          )}
          {etapa === 'cnpj' && (
            <Botao
              // A consulta já corre sozinha; o botão é para seguir sem ela —
              // Receita fora do ar, ou número que a checagem recusou.
              // Com empresa escolhida no seletor, o cadastro já existe: o que
              // falta são os valores.
              onClick={() => setEtapa(escolha === 'nova' ? 'empresa' : 'valores')}
              disabled={
                escolha === '' ||
                (escolha === 'nova' &&
                  (digitos.length !== 14 ||
                    checagem?.valido === false ||
                    (checagem?.jaCadastrado === true && checagem.cadastroAtivo !== false)))
              }
              carregando={consultar.pendente}
            >
              {escolha !== '' && escolha !== 'nova'
                ? 'Lançar valores'
                : consultar.erro === null
                  ? 'Continuar'
                  : 'Preencher manualmente'}
            </Botao>
          )}
          {etapa === 'empresa' && (
            <Botao onClick={() => setEtapa('endereco')} disabled={form.legalName.trim() === ''}>
              Continuar
            </Botao>
          )}
          {etapa === 'endereco' && (
            <Botao
              onClick={() => (comoCliente ? setEtapa('valores') : criar.mutate())}
              disabled={form.emailAddress.trim() === ''}
              carregando={criar.isPending}
            >
              {comoCliente ? 'Continuar' : 'Cadastrar empresa'}
            </Botao>
          )}
          {etapa === 'valores' && (
            <Botao
              onClick={() =>
                empresaExistente === '' ? criar.mutate() : lancarNaExistente.mutate()
              }
              // Empresa que já existe e nenhum mês digitado: não há o que fazer.
              disabled={empresaExistente !== '' && itens.length === 0}
              carregando={criar.isPending || lancarNaExistente.isPending}
            >
              {empresaExistente !== ''
                ? `Lançar ${itens.length} ${itens.length === 1 ? 'mês' : 'meses'}`
                : itens.length === 0
                  ? 'Cadastrar sem valores'
                  : `Cadastrar e lançar ${itens.length} ${itens.length === 1 ? 'mês' : 'meses'}`}
            </Botao>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Passos etapa={etapa} comValores={comoCliente} semCadastro={empresaExistente !== ''} />

        {etapa === 'cnpj' && comoCliente && (
          <>
            {/*
              A empresa já cadastrada entra por aqui: o caso comum não é
              cadastrar de novo, é lançar a apuração de quem já está na base —
              e sem este seletor o caminho era sair, achar o cliente e abrir
              outra tela.
            */}
            <CampoDeSelecao
              rotulo="Empresa"
              ajuda="Escolha uma empresa já cadastrada para só lançar valores, ou cadastre uma nova."
              value={escolha}
              onChange={(e) => setEscolha(e.target.value)}
              opcoes={[
                { valor: '', rotulo: 'Selecione…' },
                { valor: 'nova', rotulo: 'Cadastrar nova empresa' },
                ...(empresas.data?.itens ?? []).map((e) => ({
                  valor: e.id,
                  rotulo: e.legalName,
                })),
              ]}
            />

            {empresaExistente !== '' && (
              <Aviso tom="informacao" titulo="Só os valores">
                O cadastro desta empresa não muda. Ela é marcada como cliente, se ainda não for, e
                as competências que você lançar entram na apuração dela.
              </Aviso>
            )}
          </>
        )}

        {etapa === 'cnpj' && escolha === 'nova' && (
          <>
            <CampoCnpj
              rotulo={comoCliente ? 'CNPJ do cliente' : 'CNPJ da empresa'}
              required
              autoFocus
              ajuda={
                consultar.pendente
                  ? 'Consultando a Receita…'
                  : 'Assim que o número fechar, buscamos os dados na Receita e conferimos se a empresa já está cadastrada.'
              }
              value={cnpj}
              onChange={(valor) => {
                setCnpj(valor);
                setChecagem(null);
              }}
            />

            {checagem?.jaCadastrado === true && checagem.cadastroAtivo === false && (
              <Aviso tom="atencao" titulo="Esta empresa está desativada">
                <p>
                  {checagem.razaoSocialCadastrada ?? 'A empresa'} já existe na base, desativada. O
                  CNPJ é único: em vez de criar outro cadastro, reative o que existe — o histórico
                  de SPED, contrato e cronograma continua nele.
                </p>
                <div className="mt-3">
                  <Botao
                    variante="secundario"
                    tamanho="pequeno"
                    carregando={reativar.isPending}
                    onClick={() => reativar.mutate()}
                  >
                    Reativar cadastro
                  </Botao>
                </div>
                {reativar.isError && (
                  <div className="mt-3">
                    <ErroDaApi titulo="Não foi possível reativar" erro={reativar.error} />
                  </div>
                )}
              </Aviso>
            )}

            {checagem?.jaCadastrado === true && checagem.cadastroAtivo !== false && (
              <Aviso tom="informacao" titulo="Esta empresa já está cadastrada">
                <p>
                  {checagem.razaoSocialCadastrada ?? 'A empresa'} já existe na base. Cadastrar de
                  novo criaria duplicata — e o CNPJ é a chave que amarra SPED e contrato.
                </p>
                <div className="mt-3">
                  <Botao
                    variante="secundario"
                    tamanho="pequeno"
                    onClick={() => {
                      // A checagem não devolve o id; as duas listagens aceitam
                      // a busca pela URL. A de empresas busca por nome, não por
                      // CNPJ — vai a razão social que a própria checagem trouxe.
                      const destino = comoCliente
                        ? `/clientes?busca=${digitos}`
                        : `/empresas?busca=${encodeURIComponent(checagem.razaoSocialCadastrada ?? digitos)}`;
                      fechar();
                      navegar(destino);
                    }}
                  >
                    {comoCliente ? 'Ir para o cliente' : 'Ir para a empresa'}
                  </Botao>
                </div>
              </Aviso>
            )}

            {consultar.erro !== null && (
              <Aviso tom="atencao" titulo="Não consegui consultar a Receita">
                Os serviços públicos limitam o número de buscas. Você pode tentar de novo ou seguir
                e preencher os dados à mão.
                <div className="mt-3">
                  <Botao variante="secundario" tamanho="pequeno" onClick={consultar.repetir}>
                    Tentar de novo
                  </Botao>
                </div>
              </Aviso>
            )}
          </>
        )}

        {etapa === 'valores' && (
          <>
            <Aviso tom="informacao" titulo="Os valores fazem o cliente existir na operação">
              Informe o ICMS apurado de cada mês: o crédito, o deságio e o benefício saem da mesma
              conta do envio de SPED. Meses em branco ficam de fora, e o arquivo, quando chegar,
              sobrescreve o mês.
            </Aviso>

            <CamposDeValores
              anoSped={anoSped}
              aoTrocarAno={setAnoSped}
              anos={[operacao - 1, operacao - 2, operacao - 3]}
              valores={valores}
              aoMudarValor={(chave, texto) => setValores((atual) => ({ ...atual, [chave]: texto }))}
            />

            {lancarNaExistente.isError && (
              <ErroDaApi titulo="Não foi possível lançar" erro={lancarNaExistente.error} />
            )}
          </>
        )}

        {etapa === 'empresa' && (
          <>
            {consultado !== null && (
              <Aviso tom="positivo" titulo="Dados da Receita Federal">
                Situação: {consultado.situacao ?? 'não informada'}. Confira e ajuste — o cadastro
                fiscal atrasa, e quem fala com o cliente sabe mais que a base pública.
              </Aviso>
            )}

            <Campo
              rotulo="Razão social"
              required
              value={form.legalName}
              onChange={(e) => definir('legalName', e.target.value)}
            />
            <Campo
              rotulo="Nome fantasia"
              value={form.name}
              onChange={(e) => definir('name', e.target.value)}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="E-mail"
                type="email"
                required
                ajuda="Para onde vão os avisos da operação."
                value={form.emailAddress}
                onChange={(e) => definir('emailAddress', e.target.value)}
              />
              <CampoTelefone
                rotulo="Telefone"
                value={form.phone}
                onChange={(valor) => definir('phone', valor)}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <CampoDeSelecao
                rotulo="Regime tributário"
                ajuda={
                  (consultado?.regimeTributario ?? null) !== null
                    ? 'Como a Receita informa.'
                    : 'A Receita não informou — escolha, se souber.'
                }
                value={form.taxRegime}
                onChange={(e) => definir('taxRegime', e.target.value)}
                opcoes={opcoesDeRegime(form.taxRegime)}
              />
              {/* CNAE não se digita: vem da Receita. Os secundários aparecem
                no cadastro da empresa, que consulta a lista completa. */}
              <div className="flex min-w-0 flex-col gap-1.5">
                <span className="text-sm font-medium text-conteudo-secundario">CNAE principal</span>
                <span className="text-sm text-conteudo-primario">
                  {form.industryClassification === ''
                    ? 'Não informado pela Receita'
                    : form.industryClassification}
                </span>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="CAD/ICMS"
                ajuda="Inscrição estadual — vai no contrato."
                value={form.cadIcms}
                onChange={(e) => definir('cadIcms', e.target.value)}
              />
              <Campo
                rotulo="Credencial SISCRED"
                value={form.credencialSiscred}
                onChange={(e) => definir('credencialSiscred', e.target.value)}
              />
            </div>
          </>
        )}

        {etapa === 'endereco' && (
          <>
            <div className="grid gap-4 sm:grid-cols-[1fr_8rem_10rem]">
              <Campo
                rotulo="Logradouro"
                value={form.addressStreetName}
                onChange={(e) => definir('addressStreetName', e.target.value)}
              />
              <Campo
                rotulo="Número"
                value={form.addressStreetNumber}
                onChange={(e) => definir('addressStreetNumber', e.target.value)}
              />
              <Campo
                rotulo="Complemento"
                value={form.addressExtraInfo}
                onChange={(e) => definir('addressExtraInfo', e.target.value)}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-[1fr_1fr_10rem_6rem]">
              <Campo
                rotulo="Bairro"
                value={form.addressDistrict}
                onChange={(e) => definir('addressDistrict', e.target.value)}
              />
              <Campo
                rotulo="Cidade"
                value={form.addressCity}
                onChange={(e) => definir('addressCity', e.target.value)}
              />
              <Campo
                rotulo="CEP"
                inputMode="numeric"
                value={form.addressPostalCode}
                onChange={(e) => definir('addressPostalCode', e.target.value)}
              />
              <Campo
                rotulo="UF"
                maxLength={2}
                value={form.addressStateCode}
                onChange={(e) => definir('addressStateCode', e.target.value.toUpperCase())}
              />
            </div>

            {comoCliente ? (
              <Aviso tom="informacao">
                Ao cadastrar, a empresa já entra como <strong>cliente</strong>. Os fornecedores e
                assessores dela são definidos depois, na aba de Serviços.
              </Aviso>
            ) : (
              <Aviso tom="informacao">
                A empresa é cadastrada <strong>sem papel</strong> na operação. Cliente, fornecedor
                ou assessor se define depois, na aba de Serviços do cadastro.
              </Aviso>
            )}

            {criar.isError && <ErroDaApi titulo="Não foi possível cadastrar" erro={criar.error} />}
          </>
        )}
      </div>
    </Modal>
  );
}

function Passos(props: {
  readonly etapa: Etapa;
  /** O modo cliente termina nos valores; o modo empresa, no endereço. */
  readonly comValores: boolean;
  /** Empresa escolhida no seletor: o cadastro não é refeito. */
  readonly semCadastro: boolean;
}): JSX.Element {
  const nomes: readonly { id: Etapa; rotulo: string }[] = [
    { id: 'cnpj', rotulo: props.semCadastro ? 'Empresa' : 'CNPJ' },
    ...(props.semCadastro
      ? []
      : ([
          { id: 'empresa', rotulo: 'Empresa' },
          { id: 'endereco', rotulo: 'Endereço' },
        ] as const)),
    ...(props.comValores ? ([{ id: 'valores', rotulo: 'Valores' }] as const) : []),
  ];
  const atual = nomes.findIndex((n) => n.id === props.etapa);

  return (
    <ol className="flex flex-wrap gap-2" aria-label="Etapas do cadastro">
      {nomes.map((n, i) => (
        <li key={n.id} aria-current={i === atual ? 'step' : undefined}>
          <Selo tom={i === atual ? 'informativo' : i < atual ? 'positivo' : 'neutro'}>
            {i < atual ? '✓' : i + 1} {n.rotulo}
          </Selo>
        </li>
      ))}
    </ol>
  );
}
